import type { BuilderDraft } from '@/lib/crm/hooks/useBuilderSubject.helpers';
import type { PreviewOverlay } from '@/lib/crm/preview';

import { BuilderPanel } from './BuilderPanel';
import { DockedSidePanel } from './DockedSidePanel';

interface BuilderRecordPanelSharedProps {
  orgId: number | undefined;
  entityTypeName: string | undefined;
  entityId: string | undefined;
  overlays: Record<string, PreviewOverlay>;
  draft: BuilderDraft | undefined;
  onApproved: () => void;
  onRejected: () => void;
}

interface BuilderRecordPanelDesktopProps extends BuilderRecordPanelSharedProps {
  visible: boolean;
  slideoutWidth: number;
}

/**
 * Desktop placement of the object being built, beside the chat column.
 *
 * MUST render as a child of the same flex row as the chat column — the
 * element wrapping that row is `display: block`, so a sibling placed after
 * it stacks BELOW the full-height chat column and is clipped away by
 * `overflow-hidden`. Keep this call site inside that flex row.
 */
export function BuilderRecordPanelDesktop({
  visible,
  slideoutWidth,
  orgId,
  entityTypeName,
  entityId,
  overlays,
  draft,
  onApproved,
  onRejected,
}: BuilderRecordPanelDesktopProps) {
  if (!visible) {
    return null;
  }
  return (
    <DockedSidePanel width={slideoutWidth}>
      <BuilderPanel
        orgId={orgId}
        entityTypeName={entityTypeName}
        entityId={entityId}
        overlays={overlays}
        draft={draft}
        onApproved={onApproved}
        onRejected={onRejected}
      />
    </DockedSidePanel>
  );
}

interface BuilderRecordPanelMobileOverlayProps extends BuilderRecordPanelSharedProps {
  visible: boolean;
  onClosePanel: () => void;
}

/**
 * Mobile placement: the record panel, full screen over the chat.
 *
 * Rendered as a sibling of the chat column rather than inside it so it
 * covers the header too — the panel is its own surface while open, with its
 * own way back (onClosePanel), not a section of the conversation.
 */
export function BuilderRecordPanelMobileOverlay({
  visible,
  orgId,
  entityTypeName,
  entityId,
  overlays,
  draft,
  onApproved,
  onRejected,
  onClosePanel,
}: BuilderRecordPanelMobileOverlayProps) {
  if (!visible) {
    return null;
  }
  return (
    <div className='fixed inset-0 z-40 flex flex-col bg-bg-primary lg:hidden'>
      <BuilderPanel
        orgId={orgId}
        entityTypeName={entityTypeName}
        entityId={entityId}
        overlays={overlays}
        draft={draft}
        onApproved={onApproved}
        onRejected={onRejected}
        onClosePanel={onClosePanel}
      />
    </div>
  );
}
